import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import DataTable from "../../components/DataTable";
import Alert from "../../components/Alert";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { listCashOnlyLedgers } from "../../api/accountApi";
import { listPayslipsBySection, deletePayslip, collectPayslips } from "../../api/payslipApi";
import "../Attendance/Attendance.css";

const academicYearApi = createLookupApi("academic-years");
const today = () => new Date().toISOString().slice(0, 10);

/**
 * Direct port of digicampusbd's `payslip-collect-section-wise` ("Section
 * Wise Collection") — confirmed via `PayslipViewService
 * .viewMultipleSectionStudentPayslipView` this is the same pending/paid
 * payslip list + bulk collect + delete already built for `payslip-collect`
 * ("ID Wise Collection"), scoped to one section+academic-year instead of
 * institute-wide. New `GET /api/payslips/section` (one new repository
 * query) reuses the already-existing `collectPayslips`/`deletePayslip`
 * endpoints as-is. Note: the old app's own real url slug for this page has
 * a literal typo — a stray embedded space (`payslip-collect- section-wise`)
 * — the real template file and its own internal links use the space-free
 * `payslip-collect-section-wise`; both are registered to this page here.
 *
 * Debit Ledger picker uses `listCashOnlyLedgers` (Cash & Cash Equivalent only,
 * category_default_id {102}), not the generic unfiltered list — the real
 * old-app template calls the exact same `/general/accounts/report/
 * cash-equivalance/ledger/list` endpoint as the ID-Wise Collection page and
 * the fee-collection "Paid By" picker (found while sweeping Fee Management —
 * see MIGRATION.md).
 */
export default function PayslipSectionWisePage() {
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigId, setClassConfigId] = useState("");
  const [payslipStatus, setPayslipStatus] = useState("0");
  const [payslips, setPayslips] = useState(null);
  const [ledgers, setLedgers] = useState([]);
  const [debitLedgerId, setDebitLedgerId] = useState("");
  const [paymentDate, setPaymentDate] = useState(today());
  const [selectedIds, setSelectedIds] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [collecting, setCollecting] = useState(false);

  useEffect(() => {
    academicYearApi.list().then((years) => {
      setAcademicYears(years);
      if (years.length > 0) setAcademicYear(years[0].name);
    });
    listClassConfigurations().then((configs) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigId(String(configs[0].id));
    });
    listCashOnlyLedgers().then((list) => {
      setLedgers(list);
      if (list.length > 0) setDebitLedgerId(String(list[0].ledgerId));
    });
  }, []);

  const handleSearch = async () => {
    setError("");
    setSuccess("");
    if (!academicYear || !classConfigId) {
      setError("Please select academic year and section.");
      return;
    }
    try {
      const list = await listPayslipsBySection(classConfigId, academicYear, Number(payslipStatus));
      setPayslips(list);
      setSelectedIds([]);
    } catch (err) {
      setError("Failed to load payslips.");
    }
  };

  const toggle = (id) => setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));

  const handleDelete = async (payslip) => {
    if (!window.confirm(`Delete pending payslip for "${payslip.studentName}"?`)) return;
    try {
      await deletePayslip(payslip.payslipId);
      setSuccess("Payslip deleted.");
      setPayslips((prev) => prev.filter((p) => p.payslipId !== payslip.payslipId));
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to delete payslip.");
    }
  };

  const handleCollect = async () => {
    setError("");
    if (selectedIds.length === 0 || !debitLedgerId) {
      setError("Select at least one payslip and a ledger.");
      return;
    }
    setCollecting(true);
    try {
      const res = await collectPayslips({ debitLedgerId: Number(debitLedgerId), paymentDate, payslipIds: selectedIds });
      setSuccess(res.message || "Payslips collected.");
      setSelectedIds([]);
      handleSearch();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to collect payslips.");
    } finally {
      setCollecting(false);
    }
  };

  const columns = [
    { key: "select", label: "", render: (p) => <input type="checkbox" checked={selectedIds.includes(p.payslipId)} onChange={() => toggle(p.payslipId)} /> },
    { key: "customStudentId", label: "Student ID" },
    { key: "studentName", label: "Name" },
    { key: "payableAmount", label: "Payable", render: (p) => p.payableAmount.toFixed(2) },
    { key: "paidAmount", label: "Paid", render: (p) => p.paidAmount.toFixed(2) },
    { key: "dueAmount", label: "Due", render: (p) => p.dueAmount.toFixed(2) },
    { key: "createDate", label: "Created" },
  ];

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Payslip Collection — Section Wise</h1>
      </div>
      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>
      <div className="attendance-filters">
        <label>
          Academic Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Section
          <select value={classConfigId} onChange={(e) => setClassConfigId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Status
          <select value={payslipStatus} onChange={(e) => setPayslipStatus(e.target.value)}>
            <option value="0">Pending</option>
            <option value="1">Paid</option>
          </select>
        </label>
        <button type="button" className="btn-primary" onClick={handleSearch}>
          Search
        </button>
      </div>

      {payslips && (
        <>
          {payslipStatus === "0" && (
            <div className="attendance-filters">
              <label>
                Debit Ledger
                <select value={debitLedgerId} onChange={(e) => setDebitLedgerId(e.target.value)}>
                  {ledgers.map((l) => (
                    <option key={l.ledgerId} value={l.ledgerId}>
                      {l.ledgerName}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Payment Date
                <input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
              </label>
              <button type="button" className="btn-primary" onClick={handleCollect} disabled={collecting}>
                {collecting ? "Collecting..." : `Collect ${selectedIds.length} Selected`}
              </button>
            </div>
          )}
          <DataTable
            columns={columns}
            rows={payslips}
            actions={
              payslipStatus === "0"
                ? (p) => (
                    <button type="button" className="btn-link danger" onClick={() => handleDelete(p)}>
                      Delete
                    </button>
                  )
                : undefined
            }
          />
        </>
      )}
    </MainLayout>
  );
}
